/**
 * The four figures above each report.
 *
 * Derived from the rows the table is already showing rather than fetched
 * separately. Two requests for "the same" report is how a headline ends up
 * disagreeing with the table under it -- and every one of these is a count or a
 * maximum over a handful of rows, so there is nothing to gain by asking the
 * server twice.
 *
 * Each returns up to four { label, value, detail, tone } cards. `tone` names a
 * colour role rather than a colour, so the page stays in charge of the palette.
 */

const n = (v) => Number(v) || 0;
const fmt = (v) => n(v).toLocaleString('en-IN');

const NEVER = 'Never Ordered';
const ENGAGED = ['Active Recurring User', 'Repeat User'];

/** Median, because one customer with 3,000 orders drags a mean anywhere. */
function median(values) {
  const xs = values.filter((v) => Number.isFinite(v)).sort((a, b) => a - b);
  if (!xs.length) return 0;
  const mid = Math.floor(xs.length / 2);
  return xs.length % 2 ? xs[mid] : Math.round((xs[mid - 1] + xs[mid]) / 2);
}

function biggest(rows, key, valueKey) {
  let best = null;
  for (const r of rows) if (!best || n(r[valueKey]) > n(best[valueKey])) best = r;
  return best;
}

function usersBySegment(rows) {
  const total = rows.reduce((s, r) => s + n(r.users), 0);
  const never = n((rows.find((r) => r.segment === NEVER) || {}).users);
  const ordered = total - never;
  const engaged = rows
    .filter((r) => ENGAGED.includes(r.segment))
    .reduce((s, r) => s + n(r.users), 0);

  return [
    { label: 'Total users', value: fmt(total), detail: 'in this selection', tone: 'brand' },
    {
      label: 'Ordered at least once',
      value: fmt(ordered),
      detail: total ? `${((ordered / total) * 100).toFixed(1)}% of users` : '—',
      tone: 'good',
    },
    {
      label: 'Active or repeat',
      value: fmt(engaged),
      detail: 'ordered within 60 days',
      tone: 'accent',
    },
    {
      label: 'Never ordered',
      value: fmt(never),
      detail: total ? `${((never / total) * 100).toFixed(1)}%` : '—',
      tone: 'muted',
    },
  ];
}

function usersByHub(rows) {
  const total = rows.reduce((s, r) => s + n(r.users), 0);
  const top = biggest(rows, 'hub', 'users');
  const unassigned = n((rows.find((r) => r.hub === 'Unassigned') || {}).users);
  const empty = rows.filter((r) => n(r.users) === 0).length;

  return [
    { label: 'Hubs', value: fmt(rows.length), detail: 'including unassigned', tone: 'brand' },
    {
      label: 'Largest',
      value: top ? top.hub : '—',
      detail: top ? `${fmt(top.users)} users` : '—',
      tone: 'good',
    },
    {
      label: 'Unassigned',
      value: fmt(unassigned),
      detail: total ? `${((unassigned / total) * 100).toFixed(1)}% with no hub` : 'no origin hub',
      tone: 'accent',
    },
    {
      label: 'Empty hubs',
      value: fmt(empty),
      detail: empty ? 'no users yet' : 'every hub has users',
      tone: 'muted',
    },
  ];
}

function segmentByHub(rows) {
  const zeros = rows.filter((r) => n(r.users) === 0).length;
  const hubs = new Set(rows.map((r) => r.hub)).size;
  const engaged = rows
    .filter((r) => ENGAGED.includes(r.segment))
    .reduce((s, r) => s + n(r.users), 0);
  const bestRow = biggest(rows.filter((r) => ENGAGED.includes(r.segment)), 'hub', 'users');

  return [
    { label: 'Cells', value: fmt(rows.length), detail: `${hubs} hubs × 5 segments`, tone: 'brand' },
    {
      label: 'Most engaged hub',
      value: bestRow && n(bestRow.users) ? bestRow.hub : '—',
      detail: bestRow && n(bestRow.users) ? `${fmt(bestRow.users)} active or repeat` : 'none yet',
      tone: 'good',
    },
    { label: 'Active or repeat', value: fmt(engaged), detail: 'across all hubs', tone: 'accent' },
    {
      label: 'Empty cells',
      value: fmt(zeros),
      detail: rows.length ? `${((zeros / rows.length) * 100).toFixed(0)}% of the grid` : '—',
      tone: 'muted',
    },
  ];
}

function signupsByHubDay(rows) {
  const total = rows.reduce((s, r) => s + n(r.signups), 0);

  const byDay = new Map();
  for (const r of rows) byDay.set(r.date, (byDay.get(r.date) || 0) + n(r.signups));
  let bestDay = null;
  for (const [date, count] of byDay) if (!bestDay || count > bestDay[1]) bestDay = [date, count];
  const quiet = [...byDay.values()].filter((v) => v === 0).length;

  const hubsWith = new Set(rows.filter((r) => n(r.signups) > 0).map((r) => r.hub)).size;
  const hubsTotal = new Set(rows.map((r) => r.hub)).size;

  return [
    { label: 'Signups', value: fmt(total), detail: `over ${byDay.size} day${byDay.size === 1 ? '' : 's'}`, tone: 'brand' },
    {
      label: 'Best day',
      value: bestDay && bestDay[1] ? bestDay[0] : '—',
      detail: bestDay && bestDay[1] ? `${fmt(bestDay[1])} signups` : 'none in range',
      tone: 'good',
    },
    { label: 'Days with none', value: fmt(quiet), detail: `of ${byDay.size}`, tone: 'accent' },
    {
      label: 'Hubs signing up',
      value: `${hubsWith}`,
      detail: `of ${hubsTotal}`,
      tone: 'muted',
    },
  ];
}

function userList(rows) {
  const withOrders = rows.filter((r) => n(r.orders) > 0);
  const segments = new Set(rows.map((r) => r.segment)).size;

  return [
    { label: 'Listed', value: fmt(rows.length), detail: 'matching filters', tone: 'brand' },
    {
      label: 'With orders',
      value: fmt(withOrders.length),
      detail: rows.length ? `of ${fmt(rows.length)}` : '—',
      tone: 'good',
    },
    {
      label: 'Median orders',
      value: fmt(median(withOrders.map((r) => n(r.orders)))),
      detail: 'among those who ordered',
      tone: 'accent',
    },
    { label: 'Segments present', value: `${segments}`, detail: 'of 5', tone: 'muted' },
  ];
}

function ordersByCategory(rows) {
  const top = biggest(rows, 'category', 'orders');
  const buyers = rows.reduce((m, r) => Math.max(m, n(r.customers)), 0);
  const topRow = rows.find((r) => n(r.customers) === buyers);

  return [
    { label: 'Categories sold', value: fmt(rows.length), detail: 'with at least one order', tone: 'brand' },
    {
      label: 'Top category',
      value: top ? top.category : '—',
      detail: top ? `${fmt(top.orders)} orders` : '—',
      tone: 'good',
    },
    {
      label: 'Widest reach',
      value: topRow ? topRow.category : '—',
      detail: topRow ? `${fmt(topRow.customers)} customers` : '—',
      tone: 'accent',
    },
    {
      // Said plainly, because the column deliberately sums past 100%: a basket
      // holding two categories is counted under both.
      label: 'Counted per category',
      value: 'Overlaps',
      detail: 'an order can be in several',
      tone: 'muted',
    },
  ];
}

function ordersInCategory(rows) {
  const real = rows.filter((r) => r.order_id !== '—');
  const value = real.reduce((s, r) => s + n(r.amount), 0);
  const people = new Set(real.map((r) => r.mobileno).filter(Boolean)).size;
  const truncated = rows.length !== real.length;

  return [
    {
      label: 'Orders',
      value: fmt(real.length) + (truncated ? '+' : ''),
      detail: truncated ? 'most recent shown' : 'in this category',
      tone: 'brand',
    },
    { label: 'Customers', value: fmt(people), detail: 'distinct buyers', tone: 'good' },
    {
      label: 'Value',
      value: `₹${Math.round(value).toLocaleString('en-IN')}`,
      detail: 'of the orders listed',
      tone: 'accent',
    },
    {
      label: 'Average order',
      value: real.length ? `₹${Math.round(value / real.length).toLocaleString('en-IN')}` : '—',
      detail: 'across those orders',
      tone: 'muted',
    },
  ];
}

function ordersByProduct(rows) {
  const top = biggest(rows, 'product', 'orders');
  const units = rows.reduce((s, r) => s + n(r.units), 0);
  const unmeasured = rows.reduce((s, r) => s + n(r.unmeasured), 0);
  const categories = new Set(rows.map((r) => r.category)).size;

  return [
    { label: 'Products sold', value: fmt(rows.length), detail: `across ${categories} categor${categories === 1 ? 'y' : 'ies'}`, tone: 'brand' },
    {
      label: 'Best seller',
      value: top ? top.product : '—',
      detail: top ? `${fmt(top.orders)} orders` : '—',
      tone: 'good',
    },
    { label: 'Units', value: fmt(Math.round(units)), detail: 'read from the stored baskets', tone: 'accent' },
    /*
     * Said out loud rather than buried.
     *
     * A cart line whose quantity cannot be read is counted as an order and not
     * as a unit, so `units` is a floor rather than a total. A card that hid this
     * would be presenting an undercount as a fact.
     */
    unmeasured
      ? { label: 'Unmeasured lines', value: fmt(unmeasured), detail: 'counted as orders, not units', tone: 'muted' }
      : { label: 'Every line measured', value: 'Exact', detail: 'no missing quantities', tone: 'muted' },
  ];
}

function ordersInProduct(rows) {
  const real = rows.filter((r) => r.order_id !== '—');
  const lineValue = real.reduce((s, r) => s + n(r.line_value), 0);
  const basket = real.reduce((s, r) => s + n(r.order_amount), 0);
  const people = new Set(real.map((r) => r.mobileno).filter(Boolean)).size;
  const truncated = rows.length !== real.length;

  return [
    {
      label: 'Orders',
      value: fmt(real.length) + (truncated ? '+' : ''),
      detail: truncated ? 'most recent shown' : 'contained this product',
      tone: 'brand',
    },
    { label: 'Customers', value: fmt(people), detail: 'distinct buyers', tone: 'good' },
    {
      label: 'This product',
      value: `₹${Math.round(lineValue).toLocaleString('en-IN')}`,
      detail: 'its own lines only',
      tone: 'accent',
    },
    {
      /*
       * Both numbers, deliberately. A 40-rupee add-on that keeps appearing in
       * 900-rupee baskets is worth more than its own lines say, and the two
       * cards side by side are the only honest way to show that.
       */
      label: 'Baskets it was in',
      value: `₹${Math.round(basket).toLocaleString('en-IN')}`,
      detail: 'total paid for those orders',
      tone: 'muted',
    },
  ];
}

const BUILDERS = {
  users_by_segment: usersBySegment,
  users_by_hub: usersByHub,
  segment_by_hub: segmentByHub,
  signups_by_hub_day: signupsByHubDay,
  user_list: userList,
  orders_by_category: ordersByCategory,
  orders_in_category: ordersInCategory,
  orders_by_product: ordersByProduct,
  orders_in_product: ordersInProduct,
};

export function summaryFor(report, rows) {
  const build = BUILDERS[report];
  if (!build || !Array.isArray(rows) || !rows.length) return [];
  try {
    return build(rows);
  } catch (_) {
    // A summary is a convenience. If a payload shape surprises it, the table
    // below is still perfectly readable -- so drop the cards, never the page.
    return [];
  }
}

/**
 * Bars for the report, as [label, value] pairs.
 *
 * Only where a chart says something a column of numbers does not. A user list
 * is a list of people; drawing it would be decoration.
 */
export function chartFor(report, rows) {
  if (!Array.isArray(rows) || !rows.length) return [];

  if (report === 'users_by_segment') {
    return rows.map((r) => [String(r.segment || '').replace(' User', ''), n(r.users)]);
  }
  if (report === 'users_by_hub') {
    return rows.map((r) => [r.hub, n(r.users)]);
  }
  if (report === 'signups_by_hub_day') {
    const byDay = new Map();
    for (const r of rows) byDay.set(r.date, (byDay.get(r.date) || 0) + n(r.signups));
    // Date first, so a long range does not become an unreadable forest.
    return [...byDay.entries()].slice(-14);
  }
  if (report === 'orders_by_category') {
    // Top ten. Nineteen bars on one axis is a forest, and the long tail is
    // exactly the part a chart cannot say anything useful about.
    return rows.slice(0, 10).map((r) => [r.category, n(r.orders)]);
  }
  if (report === 'orders_by_product') {
    // Top ten by orders. The tail of a product catalogue is long by nature and
    // a chart of it says nothing a sorted table does not.
    return rows.slice(0, 10).map((r) => [r.product, n(r.orders)]);
  }
  if (report === 'segment_by_hub') {
    const byHub = new Map();
    for (const r of rows) byHub.set(r.hub, (byHub.get(r.hub) || 0) + n(r.users));
    return [...byHub.entries()];
  }
  return [];
}

export default summaryFor;
